import React, { useState, useEffect, useRef, useMemo, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { sparkToast, SparkModalPortal, Modal as SparkModal, Button, SparkIcon, TagColored, TooltipTrigger, InputSingleSelect, Search, Input } from '@spark';
import { data_from_server_post } from '@/shared/http/client';
import { getTextDirectionStyle } from '@/shared/utils/textUtils';
import { useAuth } from '@/features/auth/AuthContext';
import { getSparkTagVariant } from '@/features/datasets/components/DataSelection/sparkLabelColors';

/** Inline replacement for the deleted SelectWithAddOption design-system component. */
const InlineSelectWithAddOption = ({
    options = [], value, onChange, onAdd, placeholder = 'Select option',
    addButtonText = 'Add', inputPlaceholder = 'New', disabled = false,
    showAddOption = true, showSearch = true, loading = false, validateNewOption = null,
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [newValue, setNewValue] = useState('');
    const [panelStyle, setPanelStyle] = useState({});
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const normalizedOptions = useMemo(() => options.map(opt => typeof opt === 'string' ? { value: opt, label: opt } : opt), [options]);
    const existingValues = useMemo(() => normalizedOptions.map(o => o.value), [normalizedOptions]);
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return normalizedOptions;
        return normalizedOptions.filter(o => String(o.value).toLowerCase().includes(q));
    }, [normalizedOptions, search]);
    const updatePosition = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        setPanelStyle({ position: 'fixed', top: rect.bottom + 4, left: rect.left, width: rect.width });
    }, []);
    useLayoutEffect(() => { if (open) updatePosition(); }, [open, updatePosition]);
    useEffect(() => {
        if (!open) return undefined;
        const h = () => updatePosition();
        window.addEventListener('scroll', h, true);
        window.addEventListener('resize', h);
        return () => { window.removeEventListener('scroll', h, true); window.removeEventListener('resize', h); };
    }, [open, updatePosition]);
    useEffect(() => {
        if (!open) return undefined;
        const h = (e) => {
            if (triggerRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', h, true);
        return () => document.removeEventListener('mousedown', h, true);
    }, [open]);
    const isAddDisabled = !newValue || existingValues.includes(newValue) || loading;
    const handleAdd = () => { if (isAddDisabled || !onAdd) return; if (validateNewOption && !validateNewOption(newValue, existingValues)) return; onAdd(newValue); setNewValue(''); };
    const handleSelect = (v) => { onChange?.(v); setSearch(''); setOpen(false); };
    const handleToggle = () => { if (disabled) return; setOpen(p => !p); };
    return (
        <div style={{ position: 'relative', width: '100%' }}>
            <div ref={triggerRef} style={{ width: '100%' }}>
                <InputSingleSelect showLabel={false} placeholder={placeholder} text={value != null && value !== '' ? String(value) : undefined} open={open} disabled={disabled} onToggle={handleToggle} />
            </div>
            {open && !disabled && createPortal(
                <div ref={panelRef} style={{
                    ...panelStyle, zIndex: 2000, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 8,
                    maxHeight: 320, overflowY: 'auto', padding: 8, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                }}>
                    {showSearch && <Search size="small" placeholder="Search" value={search} onChange={e => setSearch(e.target.value)} onClear={() => setSearch('')} autoFocus />}
                    <div role="menu" style={{ display: 'flex', flexDirection: 'column' }}>
                        {filtered.length === 0 ? <div style={{ padding: 8, color: '#999', fontSize: 13 }}>No results</div> : filtered.map(opt => (
                            <div key={opt.value} role="menuitem" tabIndex={0}
                                style={{ padding: 8, borderRadius: 4, cursor: 'pointer', fontSize: 13, background: opt.value === value ? '#f5f5f5' : 'transparent', fontWeight: opt.value === value ? 600 : 400 }}
                                onClick={() => handleSelect(opt.value)}
                                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelect(opt.value); } }}
                            >{opt.label}</div>
                        ))}
                    </div>
                    {showAddOption && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 8, borderTop: '1px solid #e0e0e0' }}>
                            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                                <Input showLabel={false} size="small" placeholder={inputPlaceholder} value={newValue} disabled={loading || disabled}
                                    onChange={e => setNewValue(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }} />
                            </div>
                            <Button variant="primary" size="small" startIcon={<SparkIcon slug="plus" size="small" />} disabled={isAddDisabled} onClick={handleAdd}>{addButtonText}</Button>
                        </div>
                    )}
                </div>,
                document.body
            )}
        </div>
    );
};

/**
 * RelationshipCreator
 * Props:
 *   open: boolean
 *   source: { start_index, label, content }
 *   target: { start_index, label, content }
 *   onCreate: (relationship) => void
 *   onCancel: () => void
 *   relationshipTypes: [string]
 *   client_id: string
 *   dataset: string
 *   onAddType: function (optional, to refetch or update parent list)
 */
export default function RelationshipCreator({
    open,
    source,
    target,
    onCreate,
    onCancel,
    relationshipTypes,
    client_id,
    dataset,
    onAddType,
    swapSourceAndTarget,
    llm_usecase_id,
    updateRecord,
    record_id,
    allCategories,
}) {
    const [relType, setRelType] = useState();
    const [addLoading, setAddLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState('');
    const { role } = useAuth();

    useEffect(() => {
        setRelType();
    }, [open, source, target]);

    // Switch source and target
    const handleSwitchEntities = () => {
        swapSourceAndTarget();
    };

    // Confirm relationship
    const handleCreate = () => {
        if (source && target && relType) {
            onCreate({
                label: relType,
                source_start_index: source.start_index,
                target_start_index: target.start_index,
            });
        } else {
            sparkToast.error('Please fill in relationship type');
        }
    };

    // Add new relationship type
    const handleAddRelType = (client_id, dataset, newRelType) => {
        if (!newRelType || !client_id || !dataset) {
            sparkToast.error('Please fill in all fields');
            return;
        }
        setAddLoading(true);
        data_from_server_post(
            'add-relations-label-name',
            { llm_usecase_id, label: newRelType },
            (res) => {
                setAddLoading(false);
                if (res?.already_exists) {
                    sparkToast.info(`Relationship type '${newRelType}' already exists.`);
                } else if (res?.message) {
                    sparkToast.success(res.message);
                }
                setRelType(newRelType);
                if (onAddType) onAddType(); // Optionally refetch or update parent list
            },
            (err) => {
                setAddLoading(false);
                sparkToast.error('Failed to add relationship type');
            }
        );
    };

    const handleDeleteRelType = (label) => {
        if (!client_id || !dataset) return;
        setDeleteLoading(label);
        data_from_server_post(
            'delete-relations-label-name',
            { llm_usecase_id: llm_usecase_id, relation: label, dataset: dataset, record_id: record_id },
            (res) => {
                setDeleteLoading('');
                sparkToast.success(res?.message || 'Deleted');
                if (onAddType) {
                    onAddType(); // Refetch or update parent list
                    if (res?.new_record) {
                        updateRecord(res.new_record);
                    }
                }
                if (relType === label) setRelType(undefined);
            },
            (err) => {
                setDeleteLoading('');
                sparkToast.error('Failed to delete relationship type');
            }
        );
    };

    // Helper to build options with delete icon
    const buildOptions = (relationshipTypes, relType, deleteLoading, handleDeleteRelType) =>
        relationshipTypes.map(t => ({
            value: t,
            label: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{t}</span>
                    {role !== 'Viewer' ? <span
                        style={{
                            color: t === relType ? 'var(--semantic-content-tertiary)' : 'var(--semantic-action-danger-content-on-surface)',
                            marginLeft: 8,
                            cursor: t === relType ? 'not-allowed' : 'pointer',
                            opacity: deleteLoading === t ? 0.5 : 1,
                            display: 'inline-flex',
                        }}
                        onClick={e => {
                            e.stopPropagation();
                            if (t !== relType && !deleteLoading) handleDeleteRelType(t);
                        }}
                    >
                        <SparkIcon slug="trash" size="small" />
                    </span> : null}
                </div>
            ),
        }));

    return (
        <SparkModalPortal open={open} onClose={onCancel} size="wide">
            <SparkModal
                title="Create Relations"
                showFooter={false}
                showClose
                onCloseClick={onCancel}
            >
                <div style={{ fontWeight: 600, fontSize: 16, letterSpacing: 1, display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                    <SparkIcon slug="link" size="medium" style={{ marginRight: 6 }} />
                    <span>Create Relations</span>
                    <TooltipTrigger text="Swap source and target">
                        <Button
                            variant="secondary"
                            size="small"
                            startIcon={<SparkIcon slug="swap" size="small" />}
                            onClick={handleSwitchEntities}
                            style={{ borderRadius: '50%', width: 30, height: 30, marginLeft: 12 }}
                            aria-label="Swap source and target"
                        />
                    </TooltipTrigger>
                </div>
                <div style={{
                    marginBottom: 24,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    gap: 20,
                    flexWrap: 'nowrap',
                }}>
                    {/* Source Entity */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <div style={{ display: "flex", flexDirection: "row" }}>
                            <div style={{ marginBottom: 8, marginRight: 8 }}>Source Entity</div>
                            <span style={{ marginBottom: 10, display: 'inline-flex' }}>
                                <TagColored
                                    label={source?.label}
                                    variant={getSparkTagVariant(source?.label, allCategories || [])}
                                />
                            </span>
                        </div>
                        <span style={{
                            fontSize: 18,
                            padding: '10px 20px',
                            borderRadius: 10,
                            maxWidth: '100%',
                            display: 'block',
                            wordBreak: 'break-all',
                            whiteSpace: 'pre-wrap',
                            flex: 1,
                            width: '100%',
                            border: '1px solid var(--semantic-border-default)',
                            ...getTextDirectionStyle(source?.content)
                        }}>
                            <span style={{
                                wordBreak: 'break-all',
                                display: 'block',
                                whiteSpace: 'pre-wrap',
                                verticalAlign: 'bottom',
                                ...getTextDirectionStyle(source?.content)
                            }}>&quot;{source?.content}&quot;</span>
                        </span>
                    </div>

                    {/* Relationship Type */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <div style={{ marginBottom: 8 }}>Relation Type</div>
                        <InlineSelectWithAddOption
                            placeholder="Select relation type"
                            value={relType}
                            onChange={setRelType}
                            onAdd={(newRelType) => handleAddRelType(client_id, dataset, newRelType)}
                            options={buildOptions(relationshipTypes, relType, deleteLoading, handleDeleteRelType)}
                            inputPlaceholder="New"
                            addButtonText="Add"
                            showAddOption={role !== 'Viewer'}
                            loading={addLoading}
                        />
                    </div>

                    {/* Target Entity */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <div style={{ display: "flex", flexDirection: "row" }}>
                            <div style={{ marginBottom: 8, marginRight: 8 }}>Target Entity</div>
                            <span style={{ marginBottom: 10, display: 'inline-flex' }}>
                                <TagColored
                                    label={target?.label}
                                    variant={getSparkTagVariant(target?.label, allCategories || [])}
                                />
                            </span>
                        </div>
                        <span style={{
                            fontSize: 18,
                            padding: '10px 20px',
                            borderRadius: 10,
                            maxWidth: '100%',
                            display: 'block',
                            wordBreak: 'break-all',
                            whiteSpace: 'pre-wrap',
                            flex: 1,
                            width: '100%',
                            border: '1px solid var(--semantic-border-default)',
                            ...getTextDirectionStyle(target?.content)
                        }}>
                            <span style={{
                                wordBreak: 'break-all',
                                display: 'block',
                                whiteSpace: 'pre-wrap',
                                verticalAlign: 'bottom',
                                ...getTextDirectionStyle(target?.content)
                            }}>&quot;{target?.content}&quot;</span>
                        </span>
                    </div>
                </div>
                <div style={{ textAlign: 'right', marginTop: 32, marginRight: 6, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                    <Button variant="secondary" size="large" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size="large"
                        disabled={!relType}
                        onClick={handleCreate}
                    >
                        Create Relations
                    </Button>
                </div>
            </SparkModal>
        </SparkModalPortal>
    );
} 